import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	GenericNavigationPanelProps,
	SubNavigation
} from "../SubNavigation";
import { localize } from "client_legacy/ndaw/culture";
import { useShouldStartLoadingData } from "./useShouldStartLoadingData";
import { AppType } from "client/Api/Apps/common";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { EmailScreenModel, getEmailScreenModel } from "client/Api/Email/email";
import { setTemporaryIdsOnFaultedAccounts } from "client/Features/Email/Email";
import {
	convertToExchangeAccountItem,
	ExchangeAccountItem,
	fetchFolders
} from "client/Features/Email/common";
import { EmailTreeView } from "client/Features/Email/Treeview/EmailTreeView";
import { NoResults } from "client/Components";
import {
	useForceRerender,
	useSynchronizedRef,
	useUnmountedCancellation
} from "client/Hooks";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { NavigationLinkContext, NavigationLinkContextData } from "client/Contexts/NavigationLinkContext";
import { SubNavigationLink } from "./SubNavigationLink";

type EmailPanelProps = GenericNavigationPanelProps;

export const EmailPanel = (props: EmailPanelProps): ReactElement | null => {
	const {
		isVisible,
		isPinned,
		isActive
	} = props;

	const [accountItems, setAccountItems] = useState<readonly ExchangeAccountItem[] | null>(null);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [hasError, setHasError] = useState<boolean>(false);
	const [reload, screenModelVersion] = useForceRerender();

	const shouldStartLoadingData: boolean = useShouldStartLoadingData(isPinned, isVisible);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isRenderedByPage: boolean = isActive;

	const navigationLinkContextData = useMemo(
		(): NavigationLinkContextData => {
			return {
				render(props, ref): ReactElement {
					return (
						<SubNavigationLink
							{...props}
							ref={ref}
						/>
					);
				},
			};
		},
		[]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(screenModelVersion);

			if (!shouldStartLoadingData || isRenderedByPage) {
				return undefined;
			}

			setIsBusy(true);

			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setHasError(false);
					setIsBusy(true);
					const model: EmailScreenModel = await getEmailScreenModel(signal);
					setTemporaryIdsOnFaultedAccounts(model);

					setAccountItems(
						model
							.exchangeAccounts
							.map(convertToExchangeAccountItem)
					);
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}
					setIsBusy(false);
					setHasError(true);
				}

			};

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			shouldStartLoadingData,
			screenModelVersion,
			isRenderedByPage
		]
	);

	const accountItemsRef: ReadonlyRefObject<readonly ExchangeAccountItem[] | null> = useSynchronizedRef(accountItems);

	const fetchFoldersHandler = useCallback(
		async (
			smtpAddress: string,
			folderId: string,
			shouldExcludeSelf: boolean
		): Promise<readonly ExchangeAccountItem[]> => {
			if (!accountItemsRef.current) {
				throw new Error(`Account items are not fetched yet but change is requested`);
			}

			let updatedAccountItems: readonly ExchangeAccountItem[] = accountItemsRef.current;
			const result: AsyncIterable<readonly ExchangeAccountItem[]> = fetchFolders(
				accountItemsRef.current,
				smtpAddress,
				folderId,
				shouldExcludeSelf,
				globalCancellation
			);

			for await (const accounts of result) {
				setAccountItems(accounts);
				updatedAccountItems = accounts;
			}

			return updatedAccountItems;
		},
		[
			accountItemsRef,
			globalCancellation
		]
	);

	if (isRenderedByPage) {
		return null;
	}

	const renderContent = (): ReactElement | null => {
		if (isBusy || !shouldStartLoadingData) {
			return <SubNavigation.LoadingIndicator />;
		}

		return hasError
			? (
				<NoResults
					title={localize("_UnableToRetrieveItems")}
					icon="gear"
					message={localize("_WeCantRetrieveAnyItemsAtThisMomentPleaseRetry")}
					onRetry={reload}
					height="fill"
				/>
			)
			: (
				accountItems && (
					<NavigationLinkContext.Provider value={navigationLinkContextData}>
						<EmailTreeView
							accountItems={accountItems}
							doesSupportContextActions={false}
							selectedFolderWithMailbox={undefined}
							onIsLoadingChange={setIsBusy}
							onFetchFolders={fetchFoldersHandler}
							initialExpandedRootFolderId={
								accountItems
									.find(x => !x.account.isFaulted)
									?.account
									.rootFolderId
									?.id
							}
						/>
					</NavigationLinkContext.Provider>
				)
			);
	};

	return (
		<>
			<SubNavigation.Header
				kind={AppType.Email}
				title={localize("_Email")}
			/>
			<SubNavigation.Body>
				{renderContent()}
			</SubNavigation.Body>
		</>
	);
};

EmailPanel.displayName="EmailPanel";